import "react-international-phone/style.css";
import { appendPatientTimezone } from "~/utils/patient-timezone";
import type { action, loader } from "./handlers.server";

import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  Image,
  Input,
  Loader,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title
} from "@mantine/core";

import { DateInput, DatePickerInput } from "@mantine/dates";

import { useForm, zodResolver } from "@mantine/form";

import { useDisclosure } from "@mantine/hooks";

import {
  HeadersArgs,
  MetaFunction
} from "@remix-run/node";

import {
  Await,
  Form,
  isRouteErrorResponse,
  Link,
  useFetcher,
  useLoaderData,
  useParams,
  useRouteError
} from "@remix-run/react";

import {
  IconArrowLeft,
  IconAt,
  IconBook,
  IconBrandWhatsapp,
  IconCalendar,
  IconCopyCheck,
  IconDiscountCheckFilled,
  IconExclamationCircle,
  IconHome,
  IconInfoCircle,
  IconLock,
  IconMail,
  IconMapPin,
  IconPhone,
  IconRefresh,
  IconRosetteDiscount,
  IconShare,
  IconStarFilled,
  IconTrophyFilled,
  IconVideo
} from "@tabler/icons-react";

import dayjs from "dayjs";

import { Suspense, useEffect, useRef, useState } from "react";

import { PhoneInput } from "react-international-phone";

import { RefinementCtx, z } from "zod";

import { BookingAvailabilityHelp } from "~/components/booking-availability-help";

import placeholderPerson from "~/assets/misc/placeholder-person.png";

import {
  ConsultationServiceSelect,
} from "~/components/consultation-service-select";

import { DateSlot } from "~/components/date-slot";

import { DynamicFormFields } from "~/components/dynamic-form-fields";

import { ExpandableBiography } from "~/components/expandable-biography";

import { VisGoogleMap } from "~/components/google-map";

import { KikoDoctorProfileAssistant } from "~/components/kiko-doctor-profile-assistant";

import { MultiSessionScheduler } from "~/components/multi-session-scheduler";

import { PhoneNumberVerificationModal } from "~/components/phone-number-verification-modal";

import { ServiceTimeOptions } from "~/components/service-time-options";

import { Trans } from "~/components/typed-trans";

import {
  DEFAULT_OG_IMAGE_EN,
  DEFAULT_OG_IMAGE_FR,
  PAGE_TITLE
} from "~/constants/index";

import { getGenderValues } from "~/data/dropdown-values";

import { specialtyLocationPages } from "~/data/specialty-location-pages";

import { useCountdown } from "~/hooks/use-countdown";

import { useProlongedNavigationState } from "~/hooks/use-prolonged-navigation-state";

import { TranslationKey, useTranslation } from "~/hooks/use-typed-translation";


import sharedClasses from "~/routes/($lang)._index/book-doctor.module.scss";

import { loader as serviceFormLoaderFunc } from "~/routes/api.service-form/route";

import utilityClasses from "~/styles/utilities.module.scss";

import { ComboboxItemPrice, DoctorProfile, OkDataError } from "~/types";

import { FormField } from "~/types/form-field";

import {
  appendAccountAttribution,
  startPublicProfileSession,
  trackAccountEngagement,
} from "~/utils/account-engagement.client";

import { shareOrCopyLink } from "~/utils/copy-to-clipboard";

import {
  appendDoctorAnalyticsAttribution,
  completeDoctorAnalyticsJourney,
  trackDoctorFunnelEvent,
} from "~/utils/doctor-profile-analytics.client";

import { getSlotsForDay } from "~/utils/get-slots-for-day";

import { normalizeString } from "~/utils/normalize-string";

import { renderLoadingErrorData } from "~/utils/render-loading-error-data";

import NotFoundPage from "~/routes/$/route";

import classes from "~/routes/($lang).doctor.$slug/book-doctor-detail.module.scss";

function ClientOnly({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted]=useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted? <>{children}</>:null;
}

const SLOT_LOAD_TIMEOUT_MS=12_000;

function SlotLoadingFallback({
  loadingText,
  timeoutText,
  retryText,
}: {
  loadingText: string;
  timeoutText: string;
  retryText: string;
}) {
  const [timedOut, setTimedOut]=useState(false);

  useEffect(() => {
    const timeoutId=setTimeout(() => setTimedOut(true), SLOT_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  if(timedOut) {
    return (
      <Stack align="center" gap="md" mt="2rem">
        <IconExclamationCircle size={48} color="var(--mantine-color-red-6)" />
        <Text fz={16} ta="center" maw={400} c="red">
          {timeoutText}
        </Text>
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location.reload()}
          leftSection={<IconRefresh size={16} />}>
          {retryText}
        </Button>
      </Stack>
    );
  }

  return (
    <Center mt="3rem">
      <Stack align="center" gap="sm">
        <Loader size={30} color="var(--clr-primary-500)" />
        <Text fz={14} c="dimmed">
          {loadingText}
        </Text>
      </Stack>
    </Center>
  );
}

const baseSchema=(t: any) =>
  z.object({
    serviceType: z
      .string()
      .nullable() // Start as null
      .refine(val => val!==null, {
        // Don't allow form to be submitted if service type is null
        message: t("service-type-required"),
      }),
    firstName: z.string().min(1, t("first-name-required")),
    surname: z.string().min(1, t("surname-required")),
    dob: z
      .date()
      .nullable() // Start as null
      .refine(val => val!==null, {
        // Don't allow form to be submitted if dob is null
        message: t("dob-required"),
      }),
    gender: z
      .string()
      .nullable() // Start as null
      .refine(val => val!==null, {
        // Don't allow form to be submitted if gender is null
        message: t("gender-required"),
      }),
    phoneNumber: z.string(),
    whatsappPhoneNumber: z.string(),
    email: z.string().email().optional().or(z.literal("")),
    serviceDate: z
      .union([z.string(), z.date()])
      .nullable(), // Start as null
    serviceTime: z.string(),
    slotId: z.string().optional(),
    additionalInformation: z.string().min(1, t("appointment-purpose-required")),
    sessions: z.array(z.object({
      session: z.number(),
      date: z.union([z.string(), z.date()]).nullable(),
      time: z.string(),
      slotId: z.string().optional(),
      dateType: z.string().optional(),
      isPredicted: z.boolean().optional()
    })).optional(),
    additionalData: z.record(z.any()).optional(),
  });

const superRefineLogic=(t: any, data: any, ctx: RefinementCtx) => {
  const isMultiSession=
    Array.isArray(data.sessions)&&data.sessions.length>1;

  if(isMultiSession) {
    data.sessions.forEach((session: any, index: number) => {
      if(!session.date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("service-date-required"),
          path: ["sessions", index, "date"],
        });
      }

      if(!session.time) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("service-time-required"),
          path: ["sessions", index, "time"],
        });
      }
    });
  } else {
    if("serviceDate" in data&&!data.serviceDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("service-date-required"),
        path: ["serviceDate"],
      });
    }

    if("serviceTime" in data&&!data.serviceTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("service-time-required"),
        path: ["serviceTime"],
      });
    }
  }

  // Only validate the field that should be filled
  const phoneNumberHasContent=
    data.phoneNumber&&data.phoneNumber.trim().length>4;
  const isPhoneNumberDisplayed=data.phoneNumber.trim().length>0;
  const whatsappHasContent=
    data.whatsappPhoneNumber&&data.whatsappPhoneNumber.trim().length>4;
  const isWhatsappNumberDisplayed=data.whatsappPhoneNumber.trim().length>0;

  // console.log("phoneNumberHasContent:", phoneNumberHasContent);
  // console.log("whatsappHasContent:", whatsappHasContent);

  if(!phoneNumberHasContent&&isPhoneNumberDisplayed) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("at-least-one-phone-number-required"),
      path: ["phoneNumber"],
    });
    return;
  }
  if(!whatsappHasContent&&isWhatsappNumberDisplayed) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("at-least-one-phone-number-required"),
      path: ["whatsappPhoneNumber"],
    });
    return;
  }

  // If phone number has content but is too short, validate it
  if(phoneNumberHasContent&&data?.phoneNumber?.trim()?.length<11) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("phone-number-too-short"),
      path: ["phoneNumber"],
    });
  }

  // If whatsapp has content but is too short, validate it
  if(whatsappHasContent&&data?.whatsappPhoneNumber?.trim()?.length<11) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("phone-number-too-short"),
      path: ["whatsappPhoneNumber"],
    });
  }
};

const schema=(t: any) =>
  baseSchema(t).superRefine((data, ctx) => superRefineLogic(t, data, ctx));

import { getCentralizedMeta } from "~/utils/meta-helper";

const getDoctorFallbackImage = (locale: string) =>
  locale === "fr" ? DEFAULT_OG_IMAGE_FR : DEFAULT_OG_IMAGE_EN;

const getSpecialtyLandingPagePath = (label: string, locale: string) => {
  const normalizedLabel = normalizeString(label).toLocaleLowerCase();
  const page = specialtyLocationPages.find(specialtyPage =>
    specialtyPage.aliases.some(alias =>
      normalizedLabel.includes(normalizeString(alias).toLocaleLowerCase())
    )
  );

  return page
    ? `/${locale}/specialites/${page.specialtySlug}/${page.citySlug}`
    : null;
};

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];

  const image = data.image ?? getDoctorFallbackImage(data.metaLocale);
  const imageAlt = data.metaLocale === "fr"
    ? `Photo de profil de ${data.metaDoctorName}`
    : `Profile photo of ${data.metaDoctorName}`;

  return getCentralizedMeta({
    title: data.metaTitle,
    description: data.metaDescription,
    image,
    imageAlt,
    imageType: data.image?.includes("la_ruche_health_profile_optimized_")
      ? "image/jpeg"
      : undefined,
    url: data.metaOrigin + location.pathname,
    locale: data.metaLocale,
    type: "profile",
    keywords: `Ivory Coast, Côte d'Ivoire, Appointment, Rendez-vous, Affordable appointment, Rendez-vous à prix abordable, Doctor near me, Médecin à proximité, ${data.metaSpecialization}, ${data.metaLocation}, ${data.metaServices}`,
    alternateLanguages: {
      en: `${data.metaOrigin}/en${data.partnerSlug ? `/partners/${data.partnerSlug}/doctors` : "/doctor"}/${data.slug}`,
      fr: `${data.metaOrigin}/fr${data.partnerSlug ? `/partners/${data.partnerSlug}/doctors` : "/doctor"}/${data.slug}`,
    }
  });
};

export const headers = ({ actionHeaders, loaderHeaders }: HeadersArgs) => {
  if (loaderHeaders) {
    loaderHeaders.set("Cache-Control", "private, no-store");
    return loaderHeaders;
  }
  // Otherwise return action headers
  return actionHeaders;
};

export default function DoctorBookingPage() {
  const { data } = useLoaderData<typeof loader>();
  return <BookAppointmentWithDoctor key={data?.doctorProfileData?.id ?? "missing"} />;
}

function BookAppointmentWithDoctor() {
  const { t, i18n } = useTranslation();
  const routeParams = useParams();
  const slug = routeParams["doctor-slug"] ?? routeParams.slug;
  const partnerSlug = routeParams["doctor-slug"] ? routeParams.slug : undefined;
  const directoryPath = `/${i18n.language}${partnerSlug ? `/partners/${partnerSlug}/doctors` : ""}`;
  const profilePath = partnerSlug ? `${directoryPath}/${slug}` : `/${i18n.language}/doctor/${slug}`;

  const {
    data,
    ok,
    error,
    physicianSchema,
    fxRate,
  } = useLoaderData<typeof loader>();
  // console.log("data:", data, "ok:", ok, "error:", error);

  const emailRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [dateType, setDateType] = useState("slot");
  const [availability, setAvailability] = useState<{ status: "loading" | "ready" | "error"; slots: { startTime: Date | string }[] }>({ status: "loading", slots: [] });
  useEffect(() => {
    let active = true;
    setAvailability({ status: "loading", slots: [] });
    Promise.resolve(data.slotsPromise).then(result => {
      if (active) setAvailability(result?.ok
        ? { status: "ready", slots: result.data ?? [] }
        : { status: "error", slots: [] });
    }).catch(() => { if (active) setAvailability({ status: "error", slots: [] }); });
    return () => { active = false; };
  }, [data.slotsPromise]);
  const [serviceType, setServiceType] = useState<ComboboxItemPrice | null>(
    null
  );
  const [specialtiesExpanded, setSpecialtiesExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [doctor] = useState<DoctorProfile | null>(data?.doctorProfileData || null);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [promoCode, setPromoCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [isPromoCodeValid, setIsPromoCodeValid] = useState("");
  const {
    isNavigating: isSubmitting,
    setIsNavigating: setIsSubmitting,
    reset: resetIsSubmitting,
  } = useProlongedNavigationState();
  const [
    phoneNumberVerificationModalOpened,
    {
      open: openPhoneNumberVerificationModal,
      close: closePhoneNumberVerificationModal,
    },
  ] = useDisclosure(false);
  const [isAllowedToBook, setIsAllowedToBook] = useState(false);
  const { isComplete, reset: resetTimer, timeLeft } = useCountdown(90, true);
  const [noWhatsappNumber, setNoWhatsappNumber] = useState(false);
  const [locationOption, setLocationOption] = useState("search");
  const [locationCity, setLocationCity] = useState("");
  const [locationStreet, setLocationStreet] = useState("");
  const [latLng, setLatLng] = useState("");
  const [mounted, setMounted] = useState(false);
  const [showSlotResetNotice, setShowSlotResetNotice] = useState(false);
  const previousServiceTypeRef = useRef<string | null>(null);
  const profileViewTrackedRef = useRef(false);
  const bookingTrackedRef = useRef(false);
  const slotTrackedRef = useRef(false);
  const doctorSpecialties = data.doctorSpecialtiesData ?? [];
  const careAreas = doctorSpecialties;
  const displayedServices = [...(doctor?.services ?? [])].sort(
    (a, b) => Math.max(1, a.sessions ?? 1) - Math.max(1, b.sessions ?? 1)
  );
  const bookingAvailable = availability.status === "ready" && availability.slots.some(
    slot => new Date(slot.startTime).getTime() > Date.now()
  );
  const ratingSummary = doctor?.ratingSummary;
  const shouldShowRating =
    typeof ratingSummary?.average === "number" && (ratingSummary?.count ?? 0) >= 3;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!doctor?.userId || profileViewTrackedRef.current) return;
    profileViewTrackedRef.current = true;
    trackDoctorFunnelEvent("doctor_profile_viewed", {
      doctorId: doctor.userId,
      profileId: doctor.id,
      doctorSlug: slug,
      language: i18n.language,
    });
  }, [doctor?.id, doctor?.userId, i18n.language, slug]);

  useEffect(() => {
    if (!doctor?.userId) return;
    return startPublicProfileSession({
      accountId: doctor.userId,
      accountType: "doctor",
      doctorSlug: slug,
    });
  }, [doctor?.userId, slug]);

  const actionFetcher = useFetcher<typeof action>();
  const serviceFormFetcher = useFetcher<typeof serviceFormLoaderFunc>();
  const promoCodeFetcher = useFetcher<
    OkDataError<{
      isValid: boolean;
      discountPercent: number;
      services: string[];
    }>
  >();
  const doctorAsapActionFetcher = useFetcher();

  useEffect(() => {
    const response = actionFetcher.data || doctorAsapActionFetcher.data;
    if (
      !response ||
      (response as any).error ||
      !doctor?.userId ||
      bookingTrackedRef.current
    ) {
      return;
    }
    bookingTrackedRef.current = true;
    const bookingId =
      (response as any)._id ||
      (response as any).id ||
      (response as any).data?._id;
    trackAccountEngagement(
      "booking_created",
      {
        accountId: doctor.userId,
        accountType: "doctor",
        bookingId: bookingId ? String(bookingId) : undefined,
        doctorSlug: slug,
      },
      {
        eventId: bookingId ? `booking:${bookingId}` : undefined,
      }
    );
    completeDoctorAnalyticsJourney(doctor.userId);
  }, [actionFetcher.data, doctor?.userId, doctorAsapActionFetcher.data, slug]);

  const form = useForm({
    // Controlled mode allows Select to properly work when changing language and also allows service time disabling logic to work properly when service date is changed
    mode: "controlled",
    validate: zodResolver(schema(t)),
    initialValues: {
      serviceType: (doctor?.services?.length === 1
        ? doctor.services[0].value
        : null) as string | null,
      additionalInformation: "",
      firstName: "",
      surname: "",
      dob: null as Date | null,
      gender: null as string | null,
      phoneNumber: "",
      whatsappPhoneNumber: "",
      email: "",
      serviceDate: null as Date | null | string,
      serviceTime: "",
      slotId: "",
      doctorAssigned: doctor?.userId,
      language: i18n.language,
      sessions: [] as {
        session: number;
        date: Date | null;
        time: string;
        slotId?: string;
        dateType: string;
        isPredicted?: boolean;
      }[],
      additionalData: {} as Record<string, any>,
      files: [] as File[],
    },
  });

  const sessions = form.values.sessions || [];
  const sessionsCount = serviceType?.sessions ?? 1;
  const isMultiSession = sessionsCount > 1 && sessions.length > 0;

  const isFormComplete = isMultiSession
    ? sessions.every(s => s.date && s.time)
    : !!(form.values.serviceDate && form.values.serviceTime);

  const renderSlotLoadingError = (message?: string) => (
    <Stack align="center" gap="md" mt="2rem">
      <IconExclamationCircle size={48} color="var(--mantine-color-red-6)" />
      <Text fz={16} ta="center" maw={400} c="red">
        {message || t("error-loading-appointment-slots")}
      </Text>
      <Button
        type="button"
        variant="outline"
        onClick={() => window.location.reload()}
        leftSection={<IconRefresh size={16} />}>
        {t("try-again")}
      </Button>
    </Stack>
  );

  // useLogger("form", [form.values]);

  const handleFormSubmit = (values: any) => {
    import.meta.env.DEV && console.log("[DEV] ", values);
    if ((error as any)?.doctorProfileError) return;
    if (doctor?.userId) {
      trackDoctorFunnelEvent("booking_started", {
        doctorId: doctor.userId,
        profileId: doctor.id,
        doctorSlug: slug,
        bookingType: serviceType?.category,
        serviceId: serviceType?.value,
        language: i18n.language,
      });
    }
    openPhoneNumberVerificationModal();
  };

  useEffect(() => {
    if (isAllowedToBook) {
      const formData = new FormData();
      appendPatientTimezone(formData);
      Object.entries(form.values).forEach(([key, value]) => {
        if (key === "additionalData" || key === "files") return; // Handled separately
        formData.append(key, value?.toString() ?? "");
      });
      formData.append("cost", serviceType?.originalPrice?.toString() ?? "");
      formData.append("serviceTypeLabel", serviceType?.label ?? "");

      // Use Mantine form state instead of DOM scraping
      formData.append("formData", JSON.stringify(form.values.additionalData));
      
      formData.append("duration", (serviceType?.duration ?? 0).toString());
      formData.append("promoCode", promoCode);
      formData.append("category", serviceType?.category ?? "");
      formData.append("discountPercent", discountPercent?.toString() || "0");
      formData.append(
        "location",
        latLng?.length ? latLng : locationCity + " " + locationStreet
      );

      // Add multi-session dates if available
      const sessions = form.values.sessions;
      if (sessions && sessions.length > 1) {
        const appointmentDates = sessions.map((s: any) => ({
          session: s.session,
          date: s.time, // s.time is the full ISO string from the Radio value
          ...(s.slotId ? { slot_id: s.slotId } : {}),
          is_predicted: !!s.isPredicted,
        }));
        formData.append("appointment_dates", JSON.stringify(appointmentDates));
        // Use the first session as the primary appointment_date
        formData.set("serviceTime", sessions[0].time);
        formData.set("serviceDate", sessions[0].date?.toISOString() ?? "");
        formData.set("slotId", sessions[0].slotId ?? "");
      }

      // Collect and append uploaded files from form state
      const files = form.values.files || [];
      files.forEach(file => {
        formData.append("files", file);
      });
      appendAccountAttribution(formData, slug);
      if (doctor?.userId) {
        appendDoctorAnalyticsAttribution(formData, {
          doctorId: doctor.userId,
          profileId: doctor.id,
          serviceId: serviceType?.value,
          bookingType: serviceType?.category,
        });
      }

      actionFetcher.submit(formData, {
        method: "post",
        encType: "multipart/form-data",
      });
      setIsAllowedToBook(false);
    }
  }, [
    isAllowedToBook,
    form.values,
    serviceType,
    promoCode,
    discountPercent,
    latLng,
    locationCity,
    locationStreet,
    i18n.language,
    actionFetcher,
    doctorAsapActionFetcher,
  ]);

  useEffect(() => {
    if (
      serviceFormFetcher.state === "submitting" ||
      actionFetcher.state === "submitting" ||
      doctorAsapActionFetcher.state === "submitting"
    ) {
      setIsSubmitting(true);
    }
    // Only reset when both the form is no longer submitting
    // AND we've reached a new location (redirect completed)
  }, [serviceFormFetcher.state, actionFetcher.state]);

  useEffect(() => {
    const serviceTypeValue = form.values.serviceType;
    if (serviceTypeValue && doctor?.services) {
      const selectedService = doctor.services?.find(
        (item: any) => item.value === serviceTypeValue
      ) as ComboboxItemPrice;
      
      setServiceType(selectedService);
      
      if (mounted) {
        const previousServiceTypeValue = previousServiceTypeRef.current;
        const expectedCount = Math.max(1, selectedService?.sessions ?? 1);
        const serviceChanged = previousServiceTypeValue !== serviceTypeValue;

        if (serviceChanged) {
          previousServiceTypeRef.current = serviceTypeValue;
          // Automatic selection of the only service is not a patient interaction.
          const automaticallySelected = previousServiceTypeValue === null && doctor.services.length === 1;
          if (doctor?.userId && !automaticallySelected) {
            trackDoctorFunnelEvent("service_selected", {
              doctorId: doctor.userId,
              profileId: doctor.id,
              doctorSlug: slug,
              bookingType: selectedService?.category,
              serviceId: selectedService?.value,
              language: i18n.language,
            });
          }

          const nextIsMultiSession = expectedCount > 1;
          const shouldResetSchedule =
            previousServiceTypeValue !== null || nextIsMultiSession;
          const hasSelectedSlot =
            Boolean(form.values.serviceTime) ||
            form.values.sessions.some(session => Boolean(session.time));

          if (shouldResetSchedule) {
            form.setFieldValue("serviceDate", null);
            form.setFieldValue("serviceTime", "");
            form.setFieldValue("slotId", "");
            setShowSlotResetNotice(hasSelectedSlot);
            slotTrackedRef.current = false;
          } else {
            setShowSlotResetNotice(false);
          }

          if (nextIsMultiSession) {
            const initialSessions = Array.from(
              { length: expectedCount },
              (_, i) => ({
                session: i + 1,
                date: null,
                time: "",
                slotId: "",
                dateType: "slot",
                isPredicted: false,
              })
            );

            form.setFieldValue("sessions", initialSessions);
          } else if (form.values.sessions.length) {
            form.setFieldValue("sessions", []);
          }
        }
      }
    } else if (!serviceTypeValue) {
      const previousService = doctor?.services?.find(
        (item: any) => item.value === previousServiceTypeRef.current
      ) as ComboboxItemPrice | undefined;
      const previousWasMultiSession =
        Math.max(1, previousService?.sessions ?? 1) > 1;

      if (mounted && previousWasMultiSession) {
        const hadSelectedSession = form.values.sessions.some(session =>
          Boolean(session.time)
        );
        form.setFieldValue("serviceDate", null);
        form.setFieldValue("serviceTime", "");
        form.setFieldValue("slotId", "");
        form.setFieldValue("sessions", []);
        setShowSlotResetNotice(hadSelectedSession);
        slotTrackedRef.current = false;
      }
      previousServiceTypeRef.current = null;
      setServiceType(null);
    }
  }, [form.values.serviceType, doctor?.services, mounted]);

  useEffect(() => {
    const hasSelectedSlot =
      Boolean(form.values.serviceTime) ||
      form.values.sessions.some(session => Boolean(session.time));
    if (showSlotResetNotice && hasSelectedSlot) {
      setShowSlotResetNotice(false);
    }
  }, [
    form.values.serviceTime,
    form.values.sessions,
    showSlotResetNotice,
  ]);

  useEffect(() => {
    const hasSelectedSlot =
      Boolean(form.values.serviceTime) ||
      form.values.sessions.some(session => Boolean(session.time));
    if (!hasSelectedSlot || slotTrackedRef.current || !doctor?.userId) return;
    slotTrackedRef.current = true;
    trackDoctorFunnelEvent("slot_selected", {
      doctorId: doctor.userId,
      profileId: doctor.id,
      doctorSlug: slug,
      bookingType: serviceType?.category,
      serviceId: serviceType?.value,
      language: i18n.language,
    });
  }, [
    doctor?.id,
    doctor?.userId,
    form.values.serviceTime,
    form.values.sessions,
    i18n.language,
    serviceType?.category,
    slug,
  ]);

  useEffect(() => {
    if (serviceType?.value) {
      serviceFormFetcher.load(
        `/api/service-form?serviceId=${serviceType.value}`
      );
    }
  }, [serviceType?.value]);

  useEffect(() => {
    if (serviceFormFetcher.data) {
      setFormFields(serviceFormFetcher.data?.data ?? []);
    }
  }, [serviceFormFetcher.data]);

  useEffect(() => {
    if (promoCodeFetcher.data?.data && serviceType?.value) {
      const services = promoCodeFetcher.data.data?.services;
      const isValid = promoCodeFetcher.data.data?.isValid;
      // console.log(isValid, services);
      if (services?.includes(serviceType.value)) {
        // console.log(promoCodeFetcher.data.data);
        setIsPromoCodeValid(promoCodeFetcher.data?.data.isValid.toString());
        setDiscountPercent(promoCodeFetcher.data?.data.discountPercent);
      } else if (!services?.length && isValid) {
        // Repeating this block of code intentionally - Apply promocode to all services
        setIsPromoCodeValid(promoCodeFetcher.data?.data.isValid.toString());
        setDiscountPercent(promoCodeFetcher.data?.data.discountPercent);
      } else {
        setIsPromoCodeValid("false");
      }
    }
  }, [promoCodeFetcher.data, serviceType?.value]);

  useEffect(() => {
    const changed = (actionFetcher.data as any)?.priceChanged;
    if (typeof changed === "number") {
      setServiceType(previous => previous ? { ...previous, originalPrice: changed,
        price: changed * fxRate } : previous);
    }
    if ((actionFetcher.data as any)?.error || (doctorAsapActionFetcher.data as any)?.error) {
      resetIsSubmitting();
    }
  }, [actionFetcher.data]);

  const profileUrl = `https://appointment.laruche.health${profilePath}`;
  const supportMessage = serviceType?.label
    ? t("availability-help-message", { doctor: doctor?.name ?? "", service: serviceType.label, url: profileUrl })
    : t("support-whatsapp-general-message", { doctor: doctor?.name ?? "", url: profileUrl });

  return (
    <div className={classes.section}>
      {physicianSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(physicianSchema) }}
        />
      )}
      <div className={utilityClasses["small-container"]}>
        <Form
          ref={formRef}
          method="post"
          className={sharedClasses.body}
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              import.meta.env.DEV && console.log("[DEV] form errors:", errors);
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <Group justify="space-between">
            <Button
              component={Link}
              to={directoryPath}
              reloadDocument
              type="button"
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={16} />}>
              {t("go-see-specialists")}
            </Button>
            <Button
              onClick={() =>
                shareOrCopyLink(setCopied, {
                  title: `${doctor?.name ?? PAGE_TITLE} | ${PAGE_TITLE}`,
                  text: doctor?.name
                    ? `Book an appointment with ${doctor.name} on La Ruche Health.`
                    : `Book an appointment on La Ruche Health.`,
                })
              }
              variant="subtle"
              size="compact-sm"
              rightSection={
                copied ? <IconCopyCheck size={16} /> : <IconShare size={16} />
              }>
              {copied ? t("copied-link") : t("copy-link")}
            </Button>
          </Group>

          {renderLoadingErrorData({
            isLoading: false,
            loadingComponent: (
              <Center>
                <Loader size={30} />
              </Center>
            ),
            isError: !!(error as any)?.doctorProfileError,
            errorComponent: (
              <Alert
                mt="1rem"
                variant="light"
                color={"red"}
                radius={"sm"}
                icon={<IconExclamationCircle />}
                title={
                  <Text c={"red"} fw={500} fz={14} lh={1.5}>
                    {t("oops")}
                  </Text>
                }>
                <Text fz={14} lh={1.5} c={"red"}>
                  {/* {doctor?.data?.error?.status === 404
                    ? t("couldnt-find-doctor")
                    : t("error-retrieving-doctor")} */}
                  {t("error-retrieving-doctor")}
                </Text>
              </Alert>
            ),
            dataLength: Object.keys(doctor ?? {}).length,
            emptyDataComponent: <></>,
            isSuccess: ok?.doctorProfile || !!Object.keys(doctor ?? {}).length,
            dataComponent: (
              <div className={classes["doctor-profile"]}>
                
                <div className={classes["img-wrapper"]}>
                  <Image
                    src={doctor?.image ?? placeholderPerson}
                    h={{ base: 80, sm: 120 }}
                    w={{ base: 80, sm: 120 }}
                    alt={doctor?.name}
                    radius={"100vmax"}
                  />
                </div>
                <Text component="h1" fz={20} fw={600} ta={"center"} mt={"1rem"} mb={0}>
                  {doctor?.name}
                </Text>

                <Group gap="sm" mt="xs" justify="center" wrap="wrap" maw="70ch" mx="auto">
                  {doctor?.specialization && (
                    <Text fz={{ base: 13, sm: 14 }} c="dimmed" ta="center">
                      {doctor.specialization}
                    </Text>
                  )}
                  {(doctor?.consultationModes?.includes("teleconsultation") ||
                    doctor?.consultationModes?.includes("homecare")) && (
                    <Group gap="sm" justify="center" wrap="wrap">
                      {doctor?.consultationModes?.includes("teleconsultation") && (
                        <Group gap={5} wrap="nowrap">
                          <IconVideo size={15} aria-hidden="true" color="var(--clr-gray-500)" />
                          <Text fz={13} c="dimmed">{t("profile-online-consultation")}</Text>
                        </Group>
                      )}
                      {doctor?.consultationModes?.includes("homecare") && (
                        <Group gap={5} wrap="nowrap">
                          <IconHome size={15} aria-hidden="true" color="var(--clr-gray-500)" />
                          <Text fz={13} c="dimmed">{t("profile-home-visits")}</Text>
                        </Group>
                      )}
                    </Group>
                  )}
                </Group>

                <Group justify="center" gap="sm" mt="xs" wrap="wrap" maw="70ch" mx="auto">
                  {doctor?.licenseNumber && (
                    <Text fz={12} c="dimmed" ta="center">
                      {doctor.licenseNumber}
                    </Text>
                  )}
                  {doctor?.location && (
                    <Group gap={4} wrap="nowrap">
                      <IconMapPin size={14} color="var(--clr-gray-500)" style={{ flexShrink: 0 }} />
                      <Text fz={13} c="dimmed" ta="center">{doctor.location}</Text>
                    </Group>
                  )}
                </Group>

                {careAreas.length > 0 && (
                  <Box maw="70ch" mx="auto" mt="md" ta="center">
                    <Group
                      gap={5}
                      justify="center"
                      w="100%"
                      mx="auto"
                      role="group"
                      aria-label={t("profile-areas-of-care")}
                      id="doctor-specialties">
                      {(specialtiesExpanded ? careAreas : careAreas.slice(0, 4)).map((item: any, index: number) => {
                        const specialtyPath = partnerSlug ? `${directoryPath}?q=${encodeURIComponent(item.label)}` : getSpecialtyLandingPagePath(
                          item.label,
                          i18n.language
                        );

                        return specialtyPath ? (
                          <Badge
                            key={index}
                            component={Link}
                            to={specialtyPath}
                            variant="outline"
                            tt="capitalize"
                            fw={500}>
                            {item.label}
                          </Badge>
                        ) : (
                          <Badge
                            key={index}
                            variant="outline"
                            tt="capitalize"
                            fw={500}>
                            {item.label}
                          </Badge>
                        );
                      })}
                    </Group>

                    {careAreas.length > 4 && (
                      <Box mt={4}>
                        <Button type="button" variant="transparent" color="gray" size="compact-sm" px={0}
                          aria-expanded={specialtiesExpanded} aria-controls="doctor-specialties"
                          onClick={() => setSpecialtiesExpanded(value => !value)}>
                          {specialtiesExpanded ? t("profile-show-less") : t("profile-see-specialties", { amount: careAreas.length - 4 })}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
                <ExpandableBiography text={doctor?.briefDescription} />
                {!!doctor?.languages?.length && (
                  <Text c="dimmed" fz={13} ta="center" mt="sm" tt="capitalize">
                    {t("languages")}:&nbsp;
                    {doctor.languages
                      .map(lang => t(lang as TranslationKey))
                      .join(", ")}
                  </Text>
                )}
                {(shouldShowRating || !!doctor?.yearsOfExperience) && (
                  <Group justify="center" gap="sm" mt="sm" wrap="wrap">
                    {shouldShowRating && (
                      <Group gap={4}>
                        <IconStarFilled size={16} color="var(--mantine-color-yellow-5)" />
                        <Text fz={14} fw={600}>
                          {ratingSummary?.average?.toFixed(1)}
                        </Text>
                        <Text fz={13} c="dimmed">
                          ({t("x-reviews", { amount: ratingSummary?.count ?? 0 })})
                        </Text>
                      </Group>
                    )}
                    {!!doctor?.yearsOfExperience && (
                      <Badge py={10} bg="hsla(180, 50%, 95%, 1)">
                        <Group gap={5}>
                          <IconTrophyFilled size={14} color="var(--mantine-color-primary-6)" />
                          <Text c="primary.6" fz={12} fw={500} tt="capitalize">
                            {t("x-years-of-experience", { amount: doctor.yearsOfExperience })}
                          </Text>
                        </Group>
                      </Badge>
                    )}
                  </Group>
                )}
                <Suspense fallback={null}>
                  <Await resolve={data.hasMasterclassesPromise} errorElement={null}>
                    {hasMasterclasses => hasMasterclasses && (
                      <Center mt="md">
                        <Button
                          component={Link}
                          reloadDocument
                          to={`/${i18n.language}/doctor/${slug}/masterclass`}
                          variant="light"
                          color="accent"
                          style={{ borderRadius: "100vmax" }}
                          leftSection={<IconBook size={16} stroke={1.5} />}>
                          {t("view-my-masterclasses")}
                        </Button>
                      </Center>
                    )}
                  </Await>
                </Suspense>
                <Box mt="lg" maw="70ch" mx="auto" id="profile-services">
                  <ConsultationServiceSelect
                    disabled={!ok?.doctorProfile}
                    rightSection={(error as any)?.doctorProfileData
                      ? <IconExclamationCircle color="red" size={20} />
                      : null}
                    withAsterisk
                    label={t("service-type")}
                    {...form.getInputProps("serviceType")}
                    key={form.key("serviceType")}
                    data={displayedServices}
                  />
                  {(availability.status !== "ready" || !bookingAvailable) && (
                    <Box mt="sm" aria-live="polite">
                      {availability.status === "loading" ? (
                        <Text fz={14} c="dimmed">{t("loading-available-times")}</Text>
                      ) : availability.status === "error" ? renderSlotLoadingError() : (
                        <BookingAvailabilityHelp doctorName={doctor?.name ?? ""}
                          serviceName={serviceType?.label}
                          profilePath={profilePath}
                          directoryPath={directoryPath}
                          specialty={doctor?.specialization} mode={serviceType?.category}
                        />
                      )}
                    </Box>
                  )}
                </Box>

              </div>
            ),
          })}

          {bookingAvailable && (<>
          <Title id="appointment-times" tabIndex={-1} style={{ scrollMarginTop: "2rem" }} order={2} fw={600} fz={20} mt="xl">
            {t("date-and-time")}
          </Title>

          {showSlotResetNotice && (
            <Alert
              mt="sm"
              variant="light"
              color="yellow"
              radius="sm"
              icon={<IconInfoCircle size={20} />}>
              <Text fz={14}>{t("appointment-times-reset-service-change")}</Text>
            </Alert>
          )}

          <ClientOnly>
                <Suspense
                  fallback={
                    <SlotLoadingFallback
                      loadingText={t("loading-available-times")}
                      timeoutText={t("error-loading-appointment-slots")}
                      retryText={t("try-again")}
                    />
                  }
                >
                  <Await
                    resolve={data.slotsPromise}
                    errorElement={renderSlotLoadingError()}>
                    {(slotsRes) => {
                  // Handle error case with retry option
                  if (!slotsRes?.ok && slotsRes?.error) {
                    return renderSlotLoadingError(slotsRes.error.message);
                  }

                  const slotsData = slotsRes?.data?.map((item: any) => ({
                    ...item,
                    startTime: new Date(item.startTime),
                    endTime: new Date(item.endTime),
                  })) ?? [];

                  const sessions = form.values.sessions || [];
                  const sessionsCount = serviceType?.sessions ?? 1;
                  const showMultiSessionUI = sessionsCount > 1 && sessions.length > 0;

                  return (
                    <>
                      {showMultiSessionUI ? (
                        <MultiSessionScheduler
                          form={form}
                          slotsData={slotsData}
                          serviceType={serviceType}

                        />
                      ) : (
                        <>
                          {slotsData?.length ? (
                            <>
                              <Radio.Group
                                label={t("service-date")}
                                withAsterisk
                                mt={"1rem"}
                                value={dateType}
                                onChange={setDateType}>
                                <Stack>
                                  <Radio label={t("select-date-slot")} value={"slot"} />
                                  {dateType === "slot" ? (
                                    slotsData?.length ? (
                                      <Radio.Group
                                        {...form.getInputProps("serviceDate")}
                                        onChange={value => {
                                          form.setFieldValue("serviceDate", value);
                                          form.setFieldValue("serviceTime", "");
                                          form.setFieldValue("slotId", "");
                                        }}
                                        key={form.key("serviceDate")}>
                                        <Group gap={"xs"}>
                                          {Array.from(
                                            new Set(
                                              slotsData
                                                ?.map((item: any) =>
                                                  dayjs(item.startTime)
                                                    .set("hours", 0)
                                                    .set("minutes", 0)
                                                    .set("seconds", 0)
                                                    .set("milliseconds", 0)
                                                    .toISOString()
                                                )
                                                .sort(
                                                  (a: string, b: string) =>
                                                    new Date(a).getTime() -
                                                    new Date(b).getTime()
                                                )
                                            )
                                          )
                                            .slice(0, 5)
                                            .map((item: any, index: number) => (
                                              <DateSlot key={index} date={new Date(item)} />
                                            ))}
                                        </Group>
                                      </Radio.Group>
                                    ) : (
                                      <Text fz={14} c="dimmed">
                                        {t("no-available-slots")}
                                      </Text>
                                    )
                                  ) : null}
                                  <Radio label={t("select-date-manually")} value={"manual"} />
                                  {dateType === "manual" && (
                                    <DatePickerInput
                                      w={"100%"}
                                      maw={350}
                                      withAsterisk
                                      placeholder={t("select-service-date")}
                                      leftSection={<IconCalendar size={16} stroke={1.5} />}
                                      // {...form.getInputProps("serviceDate")}
                                      value={
                                        typeof form.values.serviceDate === "string"
                                          ? new Date(form.values.serviceDate as string)
                                          : (form.values.serviceDate as Date)
                                      }
                                      onChange={value => {
                                        form.setFieldValue("serviceDate", value);
                                        form.setFieldValue("serviceTime", "");
                                        form.setFieldValue("slotId", "");
                                      }}
                                      key={form.key("serviceDate")}
                                      minDate={new Date()}
                                      // maxDate={new Date(new Date().setDate(new Date().getDate() + 21))}
                                      excludeDate={(date: Date) => {
                                        // Go through each slot and compare its startTime with the given date
                                        const isSlotAvailable = slotsData?.some((slot: any) => {
                                          const slotDate = new Date(
                                            slot.startTime
                                          ).toDateString(); // Convert startTime to a Date object
                                          return slotDate === date.toDateString(); // Compare only the date part
                                        });

                                        return !isSlotAvailable; // Return true if no slots found for the given date
                                      }}
                                    />
                                  )}
                                </Stack>
                              </Radio.Group>

                              <Radio.Group
                                label={t("service-time")}
                                withAsterisk
                                mt={"1.5rem"}
                                {...form.getInputProps("serviceTime")}
                                onChange={value => {
                                  const selectedSlot = getSlotsForDay(
                                    slotsData,
                                    form.values.serviceDate as Date
                                  )?.find(slot => slot.startTime === value);
                                  form.setFieldValue("serviceTime", value);
                                  form.setFieldValue("slotId", selectedSlot?.id ?? "");
                                }}
                                key={form.key("serviceTime")}>
                                {!form.values.serviceDate ? (
                                  <Text fz={14} c="dimmed" fs={"italic"}>
                                    {t("select-date-for-options")}
                                  </Text>
                                ) : (
                                  <ServiceTimeOptions
                                    serviceDate={form.values.serviceDate as Date}
                                    slots={getSlotsForDay(
                                      slotsData,
                                      form.values.serviceDate as Date
                                    )}
                                  />
                                )}
                              </Radio.Group>
                            </>
                          ) : null}
                        </>
                      )}
                    </>
                  );
                    }}
                  </Await>
                </Suspense>
          </ClientOnly>

          <Box mt="xl" pt="md" style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}>
            <Group justify="space-between" align="center" gap="xs" wrap="wrap">
              <Stack gap={0}>
                <Text fz={12} c="dimmed">{t("support-compact-title")}</Text>
                <Text fz={13} fw={500}>{t("support-compact-action")}</Text>
              </Stack>
              <Group gap="xs" wrap="nowrap">
                <Stack align="center" gap={2} w={56}>
                  <ActionIcon component="a"
                    href={`https://wa.me/2250504783682?text=${encodeURIComponent(supportMessage)}`}
                    target="_blank" rel="noopener noreferrer"
                    aria-label={t("support-whatsapp-link")}
                    variant="subtle" color="primary" radius="xl" size={44}>
                    <IconBrandWhatsapp size={20} stroke={1.8} />
                  </ActionIcon>
                  <Text fz={11} c="dimmed">WhatsApp</Text>
                </Stack>
                <Stack align="center" gap={2} w={56}>
                  <ActionIcon component="a" href="tel:+2250504783682"
                    aria-label={t("availability-call-help")}
                    variant="subtle" color="primary" radius="xl" size={44}>
                    <IconPhone size={20} stroke={1.8} />
                  </ActionIcon>
                  <Text fz={11} c="dimmed">{t("support-call-label")}</Text>
                </Stack>
                <Stack align="center" gap={2} w={56}>
                  <ActionIcon component="a"
                    href={`mailto:rendezvous@laruche.health?subject=${encodeURIComponent(t("availability-email-subject", { doctor: doctor?.name ?? "" }))}&body=${encodeURIComponent(supportMessage)}`}
                    aria-label={t("availability-email-help")}
                    variant="subtle" color="primary" radius="xl" size={44}>
                    <IconMail size={20} stroke={1.8} />
                  </ActionIcon>
                  <Text fz={11} c="dimmed">{t("email")}</Text>
                </Stack>
              </Group>
            </Group>
          </Box>

          {serviceType?.category === "homecare" ? (
            <>
              <Title order={2} fw={500} fz={18} mt={"3rem"}>
                {t("location-details")}
              </Title>
              <Radio.Group
                withAsterisk
                mt="1rem"
                label={t("where-are-you-located")}
                value={locationOption}
                onChange={setLocationOption}>
                <Stack mt="0.5rem">
                  <Radio label={t("search-for-your-location")} value="search" />
                  {locationOption === "search" ? (
                    <>
                      <VisGoogleMap setLatLng={setLatLng} />
                    </>
                  ) : null}
                  <Radio
                    label={t("enter-location-manually")}
                    value="enter"
                    mt={"1rem"}
                  />
                  {locationOption === "enter" ? (
                    <Stack>
                      <TextInput
                        w={"calc(50% - 1rem)"}
                        label={t("city")}
                        placeholder={t("eg", { example: "Abidjan" })}
                        value={locationCity}
                        onChange={e => setLocationCity(e.target.value)}
                      />
                      <TextInput
                        w={"calc(50% - 1rem)"}
                        label={t("street-address")}
                        placeholder={t("eg", { example: "123 Avenue" })}
                        value={locationStreet}
                        onChange={e => setLocationStreet(e.target.value)}
                      />
                    </Stack>
                  ) : null}
                </Stack>
              </Radio.Group>
            </>
          ) : null}

          <Title order={2} fw={600} fz={20} mt="2.5rem">
            {t("personal-details")}
          </Title>
          <Box className={classes["data-protection-card"]} mt="sm">
            <IconLock size={26} stroke={2.5} aria-hidden="true" />
            <Text fz={14} fw={400} lh={1.5}>
              {t("data-confidentiality-description")}
            </Text>
          </Box>

          <Group
            justify="space-between"
            align="start"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            <TextInput
              w={{ base: "100%", sm: "48%" }}
              label={t("first-name")}
              placeholder={t("eg", { example: "John" })}
              {...form.getInputProps("firstName")}
              key={form.key("firstName")}
              withAsterisk
            />
            <TextInput
              w={{ base: "100%", sm: "48%" }}
              label={t("surname")}
              placeholder={t("eg", { example: "Doe" })}
              {...form.getInputProps("surname")}
              key={form.key("surname")}
              withAsterisk
            />
          </Group>
          <Group
            align="start"
            justify="space-between"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            {/* <DatePickerInput
              w={{ base: "100%", sm: "48%" }}
              label={t("dob")}
              placeholder={t("select-dob")}
              leftSection={<IconCalendar size={16} stroke={1.5} />}
              {...form.getInputProps("dob")}
              key={form.key("dob")}
              withAsterisk
              maxDate={new Date()}
            /> */}
            <DateInput
              w={{ base: "100%", sm: "48%" }}
              label={t("dob")}
              placeholder="DD/MM/YYYY"
              leftSection={<IconCalendar size={16} stroke={1.5} />}
              {...form.getInputProps("dob")}
              key={form.key("dob")}
              withAsterisk
              maxDate={new Date()}
            />
            <Select
              w={{ base: "100%", sm: "48%" }}
              label={t("gender")}
              placeholder={t("select-gender")}
              data={getGenderValues(t)}
              {...form.getInputProps("gender")}
              key={form.key("gender")}
              withAsterisk
            />
          </Group>
          <Group
            align="start"
            justify="space-between"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            <Stack gap="0.25rem" w={"48%"}>
              {noWhatsappNumber ? (
                <Input.Wrapper
                  label={t("phone-number")}
                  w={{ base: "100%" }}
                  withAsterisk>
                  <PhoneInput
                    className={classes["react-international-phone"]}
                    countrySelectorStyleProps={{
                      className: `${
                        form.errors.phoneNumber ? classes.error : ""
                      } ${
                        classes["react-international-phone-country-selector"]
                      }`,
                    }}
                    inputClassName={`${
                      form.errors.phoneNumber ? classes.error : ""
                    } ${classes["react-international-phone-input"]}`}
                    defaultCountry="ci"
                    {...form.getInputProps("phoneNumber")}
                    key={form.key("phoneNumber")}
                  />
                  <Input.Error mt={5}>{form.errors.phoneNumber}</Input.Error>
                </Input.Wrapper>
              ) : (
                <Input.Wrapper
                  label={t("whatsapp-phone-number")}
                  description={t("whatsapp-number-reason")}
                  withAsterisk
                  w={{ base: "100%" }}>
                  <PhoneInput
                    className={classes["react-international-phone"]}
                    countrySelectorStyleProps={{
                      className: `${
                        form.errors.whatsappPhoneNumber ? classes.error : ""
                      } ${
                        classes["react-international-phone-country-selector"]
                      }`,
                    }}
                    inputClassName={`${
                      form.errors.whatsappPhoneNumber ? classes.error : ""
                    } ${classes["react-international-phone-input"]}`}
                    defaultCountry="ci"
                    {...form.getInputProps("whatsappPhoneNumber")}
                    key={form.key("whatsappPhoneNumber")}
                  />
                  <Input.Error mt={5}>
                    {form.errors.whatsappPhoneNumber}
                  </Input.Error>
                </Input.Wrapper>
              )}

              <Checkbox
                label={t("no-whatsapp-number")}
                size="xs"
                checked={noWhatsappNumber}
                onChange={e => {
                  const val = e.currentTarget.checked;
                  setNoWhatsappNumber(val);
                  if (val) {
                    form.setFieldValue("whatsappPhoneNumber", "");
                  } else {
                    form.setFieldValue("phoneNumber", "");
                  }
                }}
              />
            </Stack>

            <TextInput
              w={{ base: "100%", sm: "48%" }}
              label={t("email")}
              type="email"
              leftSection={<IconAt size={16} stroke={1.5} />}
              placeholder={t("eg", { example: "johndoe@example.com" })}
              {...form.getInputProps("email")}
              key={form.key("email")}
              ref={emailRef}
            />
          </Group>

          <Textarea
            withAsterisk
            label={t("appointment-purpose")}
            mt={"3rem"}
            placeholder={t("appointment-purpose-description")}
            rows={7}
            {...form.getInputProps("additionalInformation")}
            key={form.key("additionalInformation")}
          />

          {/* Additional form fields start here --------------------------- */}
          {formFields.length ? (
            <>
              <Title order={2} fw={500} fz={18} mt={"3rem"}>
                {t("extra-info")}
              </Title>
              <DynamicFormFields fields={formFields} form={form} t={t} />
            </>
          ) : null}
          {/* Additional form fields end here ---------------------------- */}

          <Group
            align="end"
            w={{ base: "100%", sm: "calc(50% - 1rem)" }}
            mt={"3rem"}
            gap={5}>
            <TextInput
              disabled={isPromoCodeValid === "true" || !serviceType?.value}
              label={t("promo-code")}
              description={
                !serviceType?.value
                  ? t("select-service-before-promo-code")
                  : null
              }
              flex={1}
              placeholder={t("eg", { example: "ABCD123" })}
              rightSection={
                <IconRosetteDiscount size={20} stroke={1.5} color="green" />
              }
              value={promoCode}
              onChange={e => setPromoCode(e.target.value as string)}
            />
            <Button
              disabled={
                !promoCode || isPromoCodeValid === "true" || !serviceType?.value
              }
              loading={promoCodeFetcher.state === "submitting"}
              size="xs"
              variant="outline"
              type="button"
              h={36}
              onClick={() => {
                if (!promoCode || !serviceType?.value) return;
                promoCodeFetcher.submit(null, {
                  method: "post",
                  action: `/api/validate-promo-code/${promoCode}`,
                });
              }}>
              {t("apply")}
            </Button>
          </Group>

          {isPromoCodeValid === "true" ? (
            <Alert
              mt="1rem"
              variant="light"
              color={"green"}
              radius={"sm"}
              icon={<IconDiscountCheckFilled />}
              // withCloseButton
              onClose={() => setIsPromoCodeValid("")}>
              <Text fz={14} lh={1.5} c={"green"}>
                <Trans
                  i18nKey="promo-code-discount-x"
                  values={{ discountPercent }}
                  components={{ bold: <Text span inherit fw={600} /> }}
                />
              </Text>
            </Alert>
          ) : null}

          {isPromoCodeValid === "false" ? (
            <Alert
              withCloseButton
              mt="1rem"
              variant="light"
              color={"red"}
              radius={"sm"}
              icon={<IconExclamationCircle />}
              onClick={() => setIsPromoCodeValid("")}>
              <Text fz={14} lh={1.5} c={"red"}>
                {t("promo-code-invalid")}
              </Text>
            </Alert>
          ) : null}

          {actionFetcher.data && (
            <Alert
              mt="1rem"
              variant="light"
              color={"red"}
              radius={"sm"}
              icon={<IconExclamationCircle />}
              title={
                <Text c={"red"} fw={500} fz={14} lh={1.5}>
                  {t("oops")}
                </Text>
              }>
              <Text fz={14} lh={1.5} c={"red"}>
                {typeof (actionFetcher.data as any)?.error === "string" ? (actionFetcher.data as any).error : t("create-appointment-error")}
              </Text>
            </Alert>
          )}

          <Button
            type="submit"
            radius={"xl"}
            w={"100%"}
            mt={"3rem"}
            disabled={!!(error as any)?.doctorProfileError || !isFormComplete}
            loading={isSubmitting}>
            {t("confirm-appointment")}
          </Button>
          <Text fz={12} c="dimmed" fs={"italic"} mt={"0.5rem"} ta="center">
            <Trans i18nKey="agree-to-privacy-policy">
              By confirming your appointment, you agree to our{" "}
              <Anchor
                component={Link}
                target="_blank"
                to="https://docs.google.com/document/d/1amefCfTGw-erveo2ILEYqe_Ppvb0VXYDjjq0lE32f1A/edit?usp=sharing"
                inherit>
                privacy policy
              </Anchor>
              .
            </Trans>
          </Text>
          </>)}
        </Form>
        {/* Keep verification mounted while action submissions revalidate availability. */}
        <PhoneNumberVerificationModal
          whatsappPhoneNumber={
            form.values.whatsappPhoneNumber.trim().length > 3
              ? form.values.whatsappPhoneNumber
              : ""
          }
          phoneNumber={
            form.values.phoneNumber.trim().length > 3
              ? form.values.phoneNumber
              : ""
          }
          opened={phoneNumberVerificationModalOpened}
          close={closePhoneNumberVerificationModal}
          setIsAllowedToBook={(allowed, verifiedNumber, channel) => {
            if (allowed && verifiedNumber) {
              form.setFieldValue("phoneNumber", verifiedNumber);
              form.setFieldValue("whatsappPhoneNumber", channel === "whatsapp" ? verifiedNumber : "");
            }
            setIsAllowedToBook(allowed);
          }}
          timeLeft={timeLeft}
          isComplete={isComplete}
          resetTimer={resetTimer}
        />

        <KikoDoctorProfileAssistant
          disabled={!!(error as any)?.doctorProfileError}
          language={i18n.language}
          doctor={{
            partnerSlug,
            doctorId: doctor?.userId ?? "",
            profileId: doctor?.id,
            slug,
            name: doctor?.name,
            specialization: doctor?.specialization,
            specialties: doctorSpecialties,
            location: doctor?.location,
            consultationModes: doctor?.consultationModes,
            services: doctor?.services,
          }}
        />
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) {
    // todo: Replace with appropriate component
    return <NotFoundPage />;
  }
  return <Alert color="red" m="xl">Unable to load this doctor. Please refresh and try again.</Alert>;
}
